/**
 * Marka: sembol ("21" karosu), yatay logo ve yükleniyor animasyonu. Kaynak ve kurallar: BRAND.md, brand/logo/.
 * Yol verileri brand/tools/build.js tarafından aşağıdaki işaretli bloğa yazılır; elle düzenlemeyin.
 * Animasyon brand/motion/loading.svg dosyasının kendisidir (index.html açılış ekranıyla aynı kaynak).
 */
import { useId, type CSSProperties } from "react";
import { BRAND } from "@hik/i18n";
import loadingSvg from "../../../../brand/motion/loading.svg?raw";

// <marka-yollari>
const SYMBOL_VIEWBOX = "0 0 100 100";
const SYMBOL_PATH =
  "M22.5 100C10.07 100 0 89.93 0 77.5L0 22.5C0 10.07 10.07 0 22.5 0L77.5 0C89.93 0 100 10.07 100 22.5L100 77.5C100 89.93 89.93 100 77.5 100ZM52.1 73.81L52.1 63.41L32.59 63.41L32.59 62.25L42.17 57.26C45.96 55.32 48.75 53.11 50.53 50.61C52.31 48.11 53.2 45.18 53.2 41.81C53.2 38.56 52.52 35.77 51.17 33.45C49.81 31.13 47.89 29.34 45.39 28.08C42.89 26.82 39.88 26.19 36.36 26.19C32.92 26.19 29.94 26.83 27.42 28.11C24.9 29.39 22.93 31.26 21.5 33.71C20.06 36.17 19.23 39.16 19 42.68L29.34 42.68C29.76 40.56 30.58 38.93 31.8 37.81C33.02 36.68 34.56 36.12 36.42 36.12C38.32 36.12 39.77 36.62 40.77 37.6C41.78 38.59 42.28 39.99 42.28 41.81C42.28 43.25 41.91 44.52 41.15 45.65C40.4 46.77 39.21 47.77 37.58 48.66L20.16 58.65L20.16 73.81ZM60.04 33.16L60.04 42.45L72.29 34.44L72.29 73.81L81 73.81L81 27.36L69.27 27.36Z";
const WORDMARK_VIEWBOX = "0 0 240.76 100";
const WORDMARK_SYMBOL_PATH =
  "M2.31 100L2.31 69.86L36.95 50C40.19 48.23 42.55 46.23 44.05 43.99C45.55 41.76 46.31 39.22 46.31 36.37C46.31 32.76 45.3 29.96 43.3 28C41.3 26.04 38.41 25.06 34.64 25.06C30.95 25.06 27.89 26.17 25.46 28.41C23.04 30.64 21.4 33.87 20.55 38.11L0 38.11C0.46 31.1 2.12 25.15 4.97 20.26C7.81 15.38 11.74 11.66 16.74 9.12C21.75 6.58 27.68 5.31 34.53 5.31C41.53 5.31 47.52 6.56 52.48 9.06C57.45 11.57 61.28 15.13 63.97 19.75C66.67 24.36 68.01 29.91 68.01 36.37C68.01 43.07 66.24 48.9 62.7 53.87C59.16 58.83 53.62 63.24 46.07 67.09L27.02 77.02L27.02 79.33L65.82 79.33L65.82 100ZM99.98 7.62L123.31 7.62L123.31 100L105.98 100L105.98 21.71L81.62 37.64L81.62 19.17Z";
const WORDMARK_TEXT_PATH =
  "M141.44 100C141.44 100 141.44 39.32 141.44 39.32C141.44 34.57 145.29 30.72 150.05 30.72C154.8 30.72 158.65 34.57 158.65 39.32L158.65 100ZM193.88 100L176.79 100L176.79 7.62L193.88 7.62ZM219.63 30.72L237.99 30.72L237.99 31.41L213.62 63.05L240.76 99.31L240.76 100L220.55 100L199.42 70.67L199.42 57.74ZM139.02 11.03C139.02 4.94 143.96 0 150.05 0C156.14 0 161.07 4.94 161.07 11.03C161.07 17.12 156.14 22.05 150.05 22.05C143.96 22.05 139.02 17.12 139.02 11.03Z";
// </marka-yollari>

const ratio = (viewBox: string) => {
  const [, , w = 1, h = 1] = viewBox.split(" ").map(Number);
  return w / h;
};

/**
 * Marka geçişi (BRAND.md §Renk): yalnız marka yüzeylerinde. Duraklar token'lardan gelir
 * (`--c-brand-gradient-*`); yüksek kontrast modunda CSS dolguyu CanvasText yapar.
 */
function BrandGradient({ id, x1, y1, x2, y2 }: { id: string; x1: number; y1: number; x2: number; y2: number }) {
  return (
    <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
      <stop offset="0" style={{ stopColor: "var(--c-brand-gradient-start)" }} />
      <stop offset="0.4" style={{ stopColor: "var(--c-brand-gradient-start)" }} />
      <stop offset="0.83" style={{ stopColor: "var(--c-brand-gradient-mid)" }} />
      <stop offset="1" style={{ stopColor: "var(--c-brand-gradient-end)" }} />
    </linearGradient>
  );
}

/** useId değerini SVG `url(#…)` başvurusunda güvenle kullanılacak biçime getirir. */
function useSvgId(prefix: string) {
  return `${prefix}${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

/**
 * Marka sembolü: "21" karosu (`.brand-mark`, marka geçişiyle; geçiş sol alttan sağ üste).
 * Varsayılan olarak dekoratiftir (`aria-hidden`): yanında şirket adı ya da ürün adı yazar. Tek başına
 * kullanılırsa `label` verin; o zaman `role="img"` ve erişilebilir ad alır. `size` karonun kenarıdır (px).
 */
export function BrandMark({ size = 28, label }: { size?: number; label?: string }) {
  const grad = useSvgId("brand-mark-");
  return (
    <svg
      viewBox={SYMBOL_VIEWBOX}
      width={Math.round(size * ratio(SYMBOL_VIEWBOX))}
      height={size}
      className="brand-mark"
      focusable="false"
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <defs>
        <BrandGradient id={grad} x1={0} y1={1} x2={1} y2={0} />
      </defs>
      <path className="brand-mark__tile" fill={`url(#${grad})`} d={SYMBOL_PATH} />
    </svg>
  );
}

/**
 * Yatay logo: "21" (marka geçişi, soldan sağa) + "ik" (metin rengi). Yazı vektör yoldur; erişilebilir ad
 * `aria-label` ile (BRAND.name). `height` logonun yüksekliğidir (px); asgari 20 px (BRAND.md §Asgari boyut).
 */
export function BrandWordmark({ height = 28 }: { height?: number }) {
  const grad = useSvgId("brand-wordmark-");
  return (
    <svg
      viewBox={WORDMARK_VIEWBOX}
      width={Math.round(height * ratio(WORDMARK_VIEWBOX))}
      height={height}
      className="brand-wordmark"
      role="img"
      aria-label={BRAND.name}
      focusable="false"
    >
      <defs>
        <BrandGradient id={grad} x1={0} y1={0} x2={1} y2={0} />
      </defs>
      <path className="brand-wordmark__symbol" fill={`url(#${grad})`} d={WORDMARK_SYMBOL_PATH} />
      <path className="brand-wordmark__text" d={WORDMARK_TEXT_PATH} />
    </svg>
  );
}

/**
 * Tam sayfa bekleme (uygulama açılışı, oturum yüklenirken): brand/motion/loading.svg. "21" sabit
 * durur, "ik" tabandan yükselir, i'nin başı yerine oturur (1,2 s döngü; hareket azaltıldığında durağan
 * logo). index.html'deki açılış ekranıyla aynı görüntü ve aynı zaman çizelgesidir: sayfa açılışından bu
 * yana geçen süre kadar ileri sarılır, böylece JS yüklenince animasyon kesintisiz sürer. Açılıştan sonraki
 * ilk 300 ms görünmez; hızlı yanıtta hiç belirmez.
 */
export function BrandLoader() {
  const now = typeof performance === "undefined" ? 0 : performance.now();
  const style = {
    "--brand-loader-delay": `${Math.round(Math.max(0, 300 - now))}ms`,
    "--brand-loader-phase": `${-Math.round(now % 1200)}ms`,
  } as CSSProperties;
  return (
    <div className="brand-loader" role="status" aria-live="polite" style={style}>
      <span className="visually-hidden">Yükleniyor…</span>
      <div className="brand-loader__art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: loadingSvg }} />
    </div>
  );
}
